import * as React from 'react'
import { ReactElement } from 'react'
import { FormikSubFieldProps } from './FormikField'
import { InputProps } from '@fluentui/react-input'
import useCurrency from '../../hooks/useCurrency'
import FormikFormattableNumberInputField from './FormikFormattableNumberInputField'

export type FormikMoneyFieldProps<Values> = FormikSubFieldProps<InputProps, Values> & {
    /** The currency of the amount (EUR): its symbol is shown in the field and its decimals decide what can be typed. */
    currency?: string
    decimals?: number
    allowNegative?: boolean
    align?: 'start' | 'end'
    selectOnFocus?: boolean
}

/**
 * An amount field: the symbol and the number of decimals follow the currency, so an amount in a currency without
 * decimals is entered without them. Typing itself comes from FormikFormattableNumberInputField.
 *
 * This replaces the FormikField + MoneyInput combination: instead of wiring onCommit to setFieldValue (and guarding
 * against the OnLoad commit that dirtied the form) the field writes to Formik itself. To keep the old look, pass
 * `align={'end'}` and `selectOnFocus`.
 */
export default function FormikMoneyField<Values>({ currency, decimals, ...props }: FormikMoneyFieldProps<Values>): ReactElement {
    const { currency: resolvedCurrency } = useCurrency(currency)

    return (
        <FormikFormattableNumberInputField<Values>
            {...props}
            decimals={decimals ?? resolvedCurrency?.decimals ?? 2}
            componentProps={{
                // Without a currency there is nothing to show; the field is then just a number, like it was before.
                contentBefore: resolvedCurrency?.alpha ? (resolvedCurrency.symbol ?? resolvedCurrency.alpha) : undefined,
                ...props.componentProps,
            }}
        />
    )
}
